import React, { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import { PostImage } from "../../../cores/socialPost/interfaces/postImage";
import { ImagePreview } from "./creator/ImagePreview";
import "./SocialPostImagesDnd.scss";

interface Props {
  images: PostImage[];
  onReorder: (orderedNames: string[]) => void;
}

const DND_INDEX = "application/x-social-post-img-idx";

export default function SocialPostImagesDnd({ images, onReorder }: Props) {
  const intl = useIntl();
  const dragFromRef = useRef<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  const applyReorder = useCallback(
    (from: number, to: number) => {
      if (
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= images.length ||
        to >= images.length
      ) {
        return;
      }
      const next = [...images];
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed);
      onReorder(next.map((i) => i.name));
    },
    [images, onReorder]
  );

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    dragFromRef.current = index;
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(DND_INDEX, String(index));
    const cell = (e.currentTarget as HTMLElement).closest(
      ".social-post-images-dnd-wrap__cell"
    );
    if (cell && e.dataTransfer.setDragImage) {
      try {
        e.dataTransfer.setDragImage(cell as Element, 20, 20);
      } catch {
        /* noop */
      }
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    dragFromRef.current = null;
    setDraggingIndex(null);
    setDropTargetIndex(null);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dropTargetIndex !== index) setDropTargetIndex(index);
    },
    [dropTargetIndex]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData(DND_INDEX);
      const from =
        raw !== ""
          ? Number(raw)
          : dragFromRef.current ?? Number.NaN;
      dragFromRef.current = null;
      setDraggingIndex(null);
      setDropTargetIndex(null);
      if (Number.isNaN(from)) return;
      applyReorder(from, dropIndex);
    },
    [applyReorder]
  );

  if (images.length === 0) {
    return null;
  }

  if (images.length === 1) {
    return (
      <div className="social-post-images-dnd-wrap social-post-images-dnd-wrap--single">
        <ImagePreview image={images[0]} />
      </div>
    );
  }

  return (
    <div className="social-post-images-dnd-wrap social-post-images-dnd-wrap--grid">
      {images.map((image, index) => (
        <div
          key={image.name}
          className={cn("social-post-images-dnd-wrap__cell", {
            "is-dragging": draggingIndex === index,
            "is-drop-target": dropTargetIndex === index && draggingIndex !== index,
          })}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={(e) => handleDrop(e, index)}
        >
          <button
            type="button"
            className="social-post-image-drag-handle"
            draggable
            aria-label={intl.formatMessage({
              id: "app.modernImageForm.dragReorder",
            })}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </button>
          <div className="social-post-images-dnd-wrap__preview">
            <ImagePreview image={image} />
          </div>
        </div>
      ))}
    </div>
  );
}
